import type {
  CalendarException,
  CalendarId,
  ISODate,
  NonEmptyWorkingTimes,
  Project,
  WeekDayIndex,
} from '@bigint-project/core';

export const CALENDAR_CATALOG_CONTRACT_VERSION = 1 as const;
export const BIGINT_PROJECT_CLOUD_EXTENSION_KEY = 'bigintProjectCloud' as const;

export type CalendarReleaseStatus = 'Candidate' | 'Published' | 'Rejected';
export type CalendarDayStatus = 'Working' | 'Holiday';
export type CatalogDigest = (canonical: string) => string | Promise<string>;

export interface CalendarReleaseDay {
  readonly date: ISODate;
  readonly weekDay: WeekDayIndex;
  readonly status: CalendarDayStatus;
  readonly name: string | null;
}

export interface CalendarReleasePayload {
  readonly sourceKey: string;
  readonly year: number;
  readonly days: readonly CalendarReleaseDay[];
}

export interface SystemCalendarRelease {
  readonly id: string;
  readonly revisionLabel: string;
  readonly publicationStatus: CalendarReleaseStatus;
  readonly payloadSha256: string;
  readonly sourceSha256?: string;
  readonly supersedesId?: string;
  readonly payload: CalendarReleasePayload;
}

export interface CalendarPin {
  readonly id: string;
  readonly projectCalendarId: CalendarId;
  readonly sourceKey: string;
  readonly year: number;
  readonly releaseId: string;
  readonly releasePayloadSha256: string;
  readonly normalWorkingTimes: NonEmptyWorkingTimes;
  readonly managedExceptionIds: readonly string[];
  readonly managedPayloadSha256: string;
  readonly calendarBasisSha256: string;
  readonly predecessorPinId: string | null;
}

export interface MaterializedCalendarRelease {
  readonly exceptions: readonly CalendarException[];
  readonly managedExceptionIds: readonly string[];
  readonly managedPayloadSha256: string;
  readonly calendarBasisSha256: string;
}

export interface CalendarMutationResult {
  readonly project: Project;
  readonly pins: readonly CalendarPin[];
  readonly pin: CalendarPin;
}

export interface CalendarPinsExtensionV1 {
  readonly extensionVersion: 1;
  readonly calendarPins: readonly CalendarPin[];
}

export interface CalendarSourceChange {
  readonly date: ISODate;
  readonly before: CalendarReleaseDay | null;
  readonly after: CalendarReleaseDay | null;
}

export interface CalendarEffectiveChange {
  readonly date: ISODate;
  readonly beforeWorkingMinutes: number;
  readonly afterWorkingMinutes: number;
  readonly maskedByManualException: boolean;
}

export interface CalendarTaskScheduleChange {
  readonly taskId: string;
  readonly beforeStart: string;
  readonly beforeFinish: string;
  readonly afterStart: string;
  readonly afterFinish: string;
}

export interface CalendarReleaseDiff {
  readonly sourceChanges: readonly CalendarSourceChange[];
  readonly effectiveChanges: readonly CalendarEffectiveChange[];
  readonly scheduleChanges: readonly CalendarTaskScheduleChange[];
  readonly projectFinishBefore: string;
  readonly projectFinishAfter: string;
}
